import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./styles.styl";
import { useCartListHeight, useDropdownManager } from "./useDropdown";

function QuantityDropdown({
  value,
  onNumberSelect,
  numInCartClass,
  colorSecondary,
  editable,
  position,
  source,
  maxValue = 1000,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [displayedItems, setDisplayedItems] = useState(50);
  const dropdownRef = useRef(null);
  const selectedOptionRef = useRef(null);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setDisplayedItems(Math.max(50, value + 10));
    } else {
      selectedOptionRef.current?.scrollIntoView({ block: "center" });
    }
  }, [isOpen, value]);

  useDropdownManager({ isOpen, dropdownRef, setIsOpen });
  useCartListHeight({
    isOpen,
    source,
    dropdownRef,
    onClose: handleClickOutside,
  });

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (editable) {
        setIsOpen((prev) => !prev);
      }
    },
    [editable],
  );

  const handleSelect = useCallback(
    (e, num) => {
      e.stopPropagation();
      onNumberSelect(e, num);
      setIsOpen(false);
    },
    [onNumberSelect],
  );

  const handleLoadMore = useCallback((e) => {
    e.stopPropagation();
    setDisplayedItems((prev) => prev + 50);
  }, []);

  const renderDropdownOptions = () => {
    const items = Array.from({ length: displayedItems }, (_, i) => i + 1);

    return (
      <>
        {items.map((num) => (
          <div
            key={num}
            ref={num === value ? selectedOptionRef : null}
            className={classNames("quantity-dropdown__option", {
              "quantity-dropdown__option--selected": num === value,
            })}
            onClick={(e) => handleSelect(e, num)}
            role="option"
            aria-selected={num === value}
          >
            {num}
          </div>
        ))}
        {displayedItems < maxValue && (
          <div
            className="quantity-dropdown__load-more"
            onClick={handleLoadMore}
          ></div>
        )}
      </>
    );
  };

  if (!editable && !value) return null;

  return (
    <div className="quantity-dropdown" ref={dropdownRef}>
      <div
        className={classNames(numInCartClass, "quantity-dropdown__value", {
          "quantity-dropdown__value--editable": editable,
        })}
        style={{
          color: colorSecondary,
          cursor: editable ? "pointer" : "default",
        }}
        onClick={handleClick}
        role="button"
        tabIndex={editable ? 0 : -1}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{value}</span>
      </div>
      {isOpen && (
        <div
          className={classNames(
            "quantity-dropdown__list",
            `${position}-item__edit-qts-dropdown`,
          )}
          onClick={(e) => e.stopPropagation()}
          role="listbox"
          aria-label="Select quantity"
        >
          {renderDropdownOptions()}
        </div>
      )}
    </div>
  );
}

export default QuantityDropdown;
